import React, { useEffect, useMemo, useState } from 'react';
import {
  formatNumber,
  Network,
  networkIdToLabel,
  numberWithCommas,
} from '../../services/utils';
import PositionLink from '../decorative/PositionLink';
import TagIcon from '../decorative/TagIcon/TagIcon';
import { formatStrategyName } from './formattingUtils';
import {
  AutomationPositionAsset,
  AutomationSortValue,
  AutomationTableCopy,
  AutomationTableLine,
  formatAutomationStrategyRow,
  getAutomationStrategyColumns,
  getPositionAssets,
  TokenPriceIndex,
} from './strategyDetails';

interface AutomationStrategyTableProps {
  strategyId: string;
  subscriptions: any[];
  nowMs?: number;
  // Built from every subscription, not just the filtered rows, so tokens absent from this
  // table can still be priced.
  priceIndex?: TokenPriceIndex;
}

type AssetSide = 'supplied' | 'borrowed';

type SortDirection = 'desc' | 'asc';

interface SortState {
  key: string;
  direction: SortDirection;
}

// Rows with nothing to compare stay at the bottom in both directions, so an ascending
// sort never promotes "-", "Disabled" or "Not configured" above real values.
const isMissingSortValue = (value: AutomationSortValue) => value === undefined
  || value === null
  || value === ''
  || (typeof value === 'number' && Number.isNaN(value));

const compareSortValues = (a: AutomationSortValue, b: AutomationSortValue) => {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'en', { numeric: true, sensitivity: 'base' });
};

const isPresent = (value: unknown) => value !== undefined && value !== null && value !== '';

const formatTokenAmount = (value: unknown) => formatNumber(String(value ?? 0), 4, true);

const formatUsd = (value: unknown) => `$${numberWithCommas(String(value ?? 0), 2, true)}`;

const shortenIdentifier = (identifier: unknown) => {
  const value = String(identifier || '');
  if (value.length <= 12) return value;
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
};

const getIdentity = (subscription: any, selectedStrategyId: string) => {
  const positionInfo = subscription?.positionInfo || {};
  const subData = subscription?.strategyData?.decoded?.subData || {};
  const rawStrategyId = subscription?.strategy?.strategyId || selectedStrategyId;
  const owner = subscription?.owner;

  if (isPresent(positionInfo.cdpId)) {
    return { label: 'CDP', value: `#${positionInfo.cdpId}`, linkId: String(positionInfo.cdpId) };
  }
  if (isPresent(positionInfo.bondId)) {
    return { label: 'Bond', value: `#${positionInfo.bondId}`, linkId: String(positionInfo.bondId) };
  }

  const troveId = subscription?.specific?.troveId ?? subData.troveId;
  if (isPresent(troveId)) {
    return { label: 'Trove', value: shortenIdentifier(troveId) };
  }

  return {
    label: rawStrategyId.includes('eoa') ? 'EOA' : 'Smart Wallet',
    value: owner ? shortenIdentifier(owner) : 'Unknown account',
    linkId: owner,
  };
};

const PositionCell = ({
  subscription,
  strategyId,
}: {
  subscription: any;
  strategyId: string;
}) => {
  const identity = getIdentity(subscription, strategyId);
  const protocol = subscription?.protocol?.slug;
  const network = Number(subscription?.chainId);
  const canLink = Boolean(identity.linkId && protocol && Number.isFinite(network));

  return (
    <div className="automation-strategy-table__stack">
      <span className="automation-strategy-table__identity-label">{identity.label}</span>
      {canLink ? (
        <PositionLink
          className="automation-strategy-table__identity-value"
          positionId={identity.linkId}
          protocol={protocol}
          network={network}
        >
          {identity.value}
        </PositionLink>
      ) : (
        <span className="automation-strategy-table__identity-value">{identity.value}</span>
      )}
    </div>
  );
};

const AssetValue = ({
  asset,
  showUsd,
}: {
  asset: AutomationPositionAsset;
  showUsd: boolean;
}) => (
  <div className="automation-strategy-table__stack">
    <span className="automation-strategy-table__asset-amount">
      {formatTokenAmount(asset.amount)} {asset.symbol}
    </span>
    {showUsd && isPresent(asset.usdValue) && (
      <span className="automation-strategy-table__asset-usd">{formatUsd(asset.usdValue)}</span>
    )}
  </div>
);

const AssetCell = ({
  subscription,
  side,
}: {
  subscription: any;
  side: AssetSide;
}) => {
  const assets = getPositionAssets(subscription, side);
  const total = side === 'supplied'
    ? subscription?.positionInfo?.suppliedCollateralUsd
    : subscription?.positionInfo?.borrowedUsd;

  return (
    <div className="automation-strategy-table__asset-list">
      {assets.length > 0 ? assets.map(asset => (
        <AssetValue
          asset={asset}
          key={`${asset.side}-${asset.symbol}-${asset.amount}-${asset.usdValue || ''}`}
          showUsd={assets.length > 1}
        />
      )) : (
        <span className="automation-strategy-table__line">
          {side === 'borrowed' ? 'No debt' : '—'}
        </span>
      )}
      {isPresent(total) && (
        <span className="automation-strategy-table__meta">
          Total {formatUsd(total)}
        </span>
      )}
    </div>
  );
};

const TagCell = ({
  tag,
  label,
}: {
  tag: string;
  label: string;
}) => (
  <span className="automation-strategy-table__tag">
    <TagIcon tag={tag} size={24} />
    {label}
  </span>
);

const COPY_FEEDBACK_MS = 1500;

const escapeForRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const CopyAddressButton = ({ label, value }: { label: string; value: string }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = () => {
    // Unavailable over plain http and in older browsers, where the label stays plain text.
    navigator.clipboard?.writeText(value).then(() => setCopied(true), () => setCopied(false));
  };

  return (
    <button
      className={copied
        ? 'automation-strategy-table__copy automation-strategy-table__copy--copied'
        : 'automation-strategy-table__copy'}
      onClick={handleCopy}
      title={copied ? 'Copied' : `Copy ${value}`}
      type="button"
    >
      {label}
    </button>
  );
};

// Splits a line on the shortened addresses it contains so only those become copy buttons,
// leaving surrounding text (amounts, "per", tickers) as plain text.
const LineText = ({ copy, text }: { copy?: AutomationTableCopy[]; text: string }) => {
  if (!copy?.length) return <>{text}</>;
  const pattern = new RegExp(`(${copy.map(entry => escapeForRegExp(entry.label)).join('|')})`, 'g');
  return (
    <>
      {text.split(pattern).map((part, index) => {
        const target = copy.find(entry => entry.label === part);
        return target ? (
          // eslint-disable-next-line react/no-array-index-key
          <CopyAddressButton key={`${part}-${index}`} label={part} value={target.value} />
        ) : part;
      })}
    </>
  );
};

const FormattedCell = ({ lines }: { lines: AutomationTableLine[] | undefined }) => (
  <div className="automation-strategy-table__stack">
    {lines?.length ? lines.map(line => (
      <div className="automation-strategy-table__stack" key={`${line.text}-${line.meta || ''}`}>
        <span className={line.tone
          ? `automation-strategy-table__status automation-strategy-table__status--${line.tone}`
          : 'automation-strategy-table__line'}
        >
          <LineText copy={line.copy} text={line.text} />
        </span>
        {line.meta && <span className="automation-strategy-table__meta">{line.meta}</span>}
      </div>
    )) : <span className="automation-strategy-table__line">—</span>}
  </div>
);

const readableStrategyId = (strategyId: string) => strategyId
  .split('-')
  .map(part => (part.toLowerCase() === 'eoa' ? 'EOA' : `${part.charAt(0).toUpperCase()}${part.slice(1)}`))
  .join(' ');

const AutomationStrategyTable = ({
  strategyId,
  subscriptions,
  nowMs,
  priceIndex,
}: AutomationStrategyTableProps) => {
  const columns = getAutomationStrategyColumns(strategyId);
  const formattedName = subscriptions.length > 0 ? formatStrategyName(subscriptions[0]) : '';
  const strategyName = formattedName || readableStrategyId(strategyId);
  const accessibleName = `${strategyName} automation subscriptions`;
  // null keeps the order the API already sorted by (supplied collateral, descending).
  const [sort, setSort] = useState<SortState | null>(null);

  // Resolved once per mount so a re-render can't shift "now" under the cadence columns.
  const [defaultNowMs] = useState(() => Date.now());
  const resolvedNowMs = nowMs ?? defaultNowMs;

  const rows = useMemo(
    () => subscriptions.map(subscription => ({
      subscription,
      row: formatAutomationStrategyRow(subscription, resolvedNowMs, priceIndex),
    })),
    [subscriptions, resolvedNowMs, priceIndex],
  );

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    // Array.prototype.sort is stable, so ties keep the default order as a tiebreaker.
    return [...rows].sort((left, right) => {
      const a = left.row.sortValues[sort.key];
      const b = right.row.sortValues[sort.key];
      const aMissing = isMissingSortValue(a);
      const bMissing = isMissingSortValue(b);
      if (aMissing || bMissing) {
        if (aMissing && bMissing) return 0;
        return aMissing ? 1 : -1;
      }
      const comparison = compareSortValues(a, b);
      return sort.direction === 'desc' ? -comparison : comparison;
    });
  }, [rows, sort]);

  // First click on a column sorts descending; clicking the same column again flips to ascending.
  const toggleSort = (key: string) => setSort(current => (
    current && current.key === key && current.direction === 'desc'
      ? { key, direction: 'asc' }
      : { key, direction: 'desc' }
  ));

  return (
    <div
      aria-label={`${accessibleName} table region`}
      className="automation-strategy-table"
      role="region"
      // The overflow region must be keyboard-focusable so wide tables can be scrolled.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
    >
      <table>
        <caption>{accessibleName}</caption>
        <thead>
          <tr>
            {columns.map((column) => {
              const direction = sort && sort.key === column.key ? sort.direction : undefined;
              return (
                <th
                  aria-sort={direction && (direction === 'asc' ? 'ascending' : 'descending')}
                  key={column.key}
                  scope="col"
                >
                  {column.sortable ? (
                    <button
                      className="automation-strategy-table__sort"
                      onClick={() => toggleSort(column.key)}
                      type="button"
                    >
                      {column.label}
                    </button>
                  ) : column.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.length > 0 ? sortedRows.map(({ subscription, row }, rowIndex) => (
            <tr key={subscription?.key || subscription?.subId || `${subscription?.positionId || 'position'}-${rowIndex}`}>
              {columns.map(column => {
                let contents: React.ReactNode;
                if (column.key === 'position') {
                  contents = <PositionCell subscription={subscription} strategyId={strategyId} />;
                } else if (column.key === 'supplied') {
                  contents = <AssetCell subscription={subscription} side="supplied" />;
                } else if (column.key === 'borrowed') {
                  contents = <AssetCell subscription={subscription} side="borrowed" />;
                } else if (column.key === 'protocol') {
                  const label = subscription?.protocol?.fullName
                      || subscription?.protocol?.name
                      || 'Unknown protocol';
                  contents = (
                    <TagCell
                      label={label}
                      tag={subscription?.protocol?.id || subscription?.protocol?.slug || ''}
                    />
                  );
                } else if (column.key === 'network') {
                  const label = networkIdToLabel(subscription?.chainId as Network)
                      || `Chain ${subscription?.chainId ?? 'unknown'}`;
                  contents = <TagCell label={label} tag={label} />;
                } else {
                  contents = <FormattedCell lines={row.cells[column.key]} />;
                }

                return (
                  <td
                    className={column.numeric ? 'automation-strategy-table__numeric' : undefined}
                    data-label={column.label}
                    key={column.key}
                  >
                    {contents}
                  </td>
                );
              })}
            </tr>
          )) : (
            <tr>
              <td className="automation-strategy-table__empty" colSpan={columns.length}>
                No subscriptions match the selected filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AutomationStrategyTable;
